import React, { useEffect, useMemo, useState } from "react";
import PermissionButton from "../shared/PermissionButton";
import {
  useAssessmentWithScaleOrComment,
  useDataForRecording,
  useStudentSubjectAssessments,
  useYearLevelByAssessmentSubjectConfig,
} from "../../hooks/useSubjectAssessment";
import { useSelectClassrooms } from "../../hooks/useOptionalSubjects";
import Button from "../shared/Button";
import { Spinner } from "reactstrap";
import {
  checkRolePermissions,
  checkSubsTeacherPermissions,
  URL,
} from "../../../../utils/Constants";
import StudentImg from "../../assets/images/student.png";
import SubjectAssessmentTableRow from "./SubjectAssessmentTableRow";
import Select from "../shared/Select";
import ReportGeneration from "./ReportGeneration";
import CustomModal from "../shared/CustomModal";
import { useARRCode } from "../../context/ARRLevelContext";

const SubjectAssessment = ({ name }) => {
  const { arrLevel } = useARRCode();
  const { yearLevel, yearLoading } =
    useYearLevelByAssessmentSubjectConfig(name);
  const { classrooms, classroomLoading, getClassrooms } = useSelectClassrooms();
  const { assessments, assessmentsLoading, getAssessmentWithScaleOrComment } =
    useAssessmentWithScaleOrComment();
  const { fetching, subjects, setSubjects, getDataForRecording } =
    useDataForRecording();
  const {
    loading,
    insertMultipleStudentSubjectAssessments,
    updateStudentSubjectAssessmentIsFinalized,
  } = useStudentSubjectAssessments(getDataForRecording);

  const [studentSubjectAssessments, setStudentSubjectAssessments] = useState(
    [],
  );
  const [resetModal, setResetModal] = useState(false);
  const [resetSubject, setResetSubject] = useState(null);
  const [formData, setFormData] = useState({});
  const [subjectIdLoader, setSubjectIdLoader] = useState(null);
  const [permissions, setPermissions] = useState({
    finalizeReportPermission: false,
    unfinalizeReportPermission: false,
  });

  const yearLevelOption = useMemo(() => {
    if (yearLevel?.length === 0) {
      return [];
    }

    return yearLevel?.map((item) => ({
      educationLevelId: item?.educationLevelId,
      label: item?.name,
      value: item?.id,
    }));
  }, [yearLevel]);

  //extract every students from every subjects and remove duplicate
  const studentList = useMemo(() => {
    if (!subjects?.subjects) return [];

    return subjects.subjects.reduce((acc, item) => {
      if (item?.students) {
        item.students.forEach((student) => {
          if (!acc.some((a) => a.admissionId === student.admissionId)) {
            acc.push(student);
          }
        });
      }

      return acc;
    }, []);
  }, [subjects]);

  //extract every subjects's assessment scale
  const subjectAssessmentScale = useMemo(() => {
    if (!subjects?.subjects) return {};

    return subjects.subjects.reduce((acc, item) => {
      const key = item.id;

      if (!acc[key]) {
        acc[key] = {};
      }

      acc[key] = item.scale;

      return acc;
    }, {});
  }, [subjects]);

  //handle change function to edit marks/records
  const handleChange = (
    admissionId,
    subjectId,
    field,
    value,
    type,
    scaleId,
    assessmentScaleMarkId,
  ) => {
    setStudentSubjectAssessments((prev) => {
      const index = prev.findIndex(
        (item) =>
          item.admissionId === admissionId && item.subjectId === subjectId,
      );

      const data = [...prev];

      const current =
        index === -1
          ? { admissionId, subjectId, scaleId, isChanged: true }
          : { ...data[index], isChanged: true };

      if (type === "theory") {
        if (field === "isAbsent") {
          current.theory = {
            ...current.theory,
            scaleMarkId: null,
            scaleGpId: null,
            scaleLevelId: null,
            obtainedMarks: null,
            [field]: value,
          };
        } else {
          current.theory = {
            ...current.theory,
            scaleMarkId: assessmentScaleMarkId ?? null,
            scaleGpId: null,
            scaleLevelId: null,
            isAbsent: false,
            [field]: value,
          };
        }
      }

      if (type === "practical") {
        if (field === "isAbsent") {
          current.practical = {
            ...current.practical,
            obtainedMarks: null,
            scaleMarkId: null,
            scaleGpId: null,
            scaleLevelId: null,
            [field]: value,
          };
        } else {
          current.practical = {
            ...current.practical,
            scaleMarkId: assessmentScaleMarkId ?? null,
            scaleGpId: null,
            scaleLevelId: null,
            isAbsent: false,
            [field]: value,
          };
        }
      }

      // update rows
      if (index === -1) {
        data.push(current);
      } else {
        data[index] = current;
      }

      return data;
    });
  };

  //check whether subjects are finalized or not
  const areSubjectsFinalized = useMemo(() => {
    return subjects?.subjects?.every((item) => item.finalized) ?? false;
  }, [subjects]);

  //save or finalize assessment records handler
  const handleSaveAssessmentRecords = (type, subjectId, finalized) => {
    if (
      !formData?.assessment?.value ||
      !formData?.yearLevel?.value ||
      !formData?.classroom?.value
    ) {
      return;
    }

    const newData = studentSubjectAssessments?.filter((item) => item.isChanged);

    const params = {
      assessmentId: formData?.assessment?.value,
      classroomId: formData?.classroom?.value,
      yearLevelId: formData?.yearLevel?.value,
      educationLevelId: formData?.educationLevelId,
      educationLevelDivisionId: formData?.educationLevelDivisionId,
      studentSubjectAssessments: newData,
    };

    if (type === "save") {
      //save assessment records
      insertMultipleStudentSubjectAssessments(params);
    } else {
      if (finalized) {
        //unfinalize without saving
        const params = {
          assessmentId: formData?.assessment?.value,
          classroomId: formData?.classroom?.value,
          yearLevelId: formData?.yearLevel?.value,
          educationLevelId: formData?.educationLevelId,
          educationLevelDivisionId: formData?.educationLevelDivisionId,
        };

        updateStudentSubjectAssessmentIsFinalized(params, subjectId, finalized);
        return;
      }
      //save first then finalize
      insertMultipleStudentSubjectAssessments(params, subjectId, finalized);
    }
  };

  //reset subject records
  const handleResetSubjectRecord = () => {
    const updated = studentSubjectAssessments?.map((item) => {
      if (item?.subjectId !== resetSubject?.id) return item;

      return {
        ...item,
        isChanged: true,
        ...(resetSubject?.hasTheory && {
          theory: null,
        }),
        ...(resetSubject?.hasPractical && {
          practical: null,
        }),
      };
    });
    setStudentSubjectAssessments(updated);
    setResetModal(false);
    setResetSubject(null);
  };

  //populating backend data
  useEffect(() => {
    if (!subjects?.studentSubjectAssessments?.length) return;

    const studentSubjectAssessments = subjects?.studentSubjectAssessments?.map(
      (item) => ({ ...item }),
    );

    setStudentSubjectAssessments(studentSubjectAssessments);
  }, [subjects]);

  //permission
  const checkPermissons = () => {
    setPermissions({
      finalizeReportPermission:
        checkRolePermissions("finalize-subject-assessment", "activity", name) ||
        checkSubsTeacherPermissions(
          "finalize-subject-assessment",
          "activity",
          name,
        ),
      unfinalizeReportPermission:
        checkRolePermissions(
          "unfinalize-subject-assessment",
          "activity",
          name,
        ) ||
        checkSubsTeacherPermissions(
          "unfinalize-subject-assessment",
          "activity",
          name,
        ),
    });
  };

  useEffect(() => {
    checkPermissons();
    setFormData({});
    setSubjects([]);
  }, [arrLevel]);

  return (
    <>
      <div className="arr-content">
        <div className="arr-heading">
          <h1>Subject Assessment</h1>
          <div className="arr-btns-group">
            <PermissionButton
              moduleName={"Recording"}
              widgetName={name}
              header={"Subject Assessment"}
              activityName={"select-subject-assessment"}
            />
          </div>
        </div>

        <form style={{ marginBottom: "24px" }} className="arr-form grid">
          <Select
            name="yearLevel"
            value={formData?.yearLevel || null}
            onChange={(option) => {
              setFormData({
                yearLevel: option,
                educationLevelId: option?.educationLevelId,
                classroom: "",
                assessment: "",
              });
              setSubjects([]);
              getClassrooms(option?.value);
            }}
            isLoading={yearLoading}
            options={yearLevelOption}
            placeholder={"Select Class"}
            isClearable={false}
          />

          <Select
            name="classroom"
            value={formData?.classroom || null}
            onChange={(option) => {
              setFormData((prev) => ({
                ...prev,
                classroom: option,
                assessment: "",
              }));
              setSubjects([]);
              const params = {
                yearLevelId: formData?.yearLevel?.value,
                educationLevelId: formData?.educationLevelId,
                classroomId: option?.value,
              };
              getAssessmentWithScaleOrComment(params);
            }}
            isDisabled={!formData?.yearLevel?.value}
            isClearable={false}
            isLoading={classroomLoading}
            options={classrooms}
            placeholder={"Select Section"}
          />

          <Select
            name="assessment"
            value={formData?.assessment || null}
            onChange={(option) => {
              setFormData((prev) => ({
                ...prev,
                assessment: option,
                educationLevelDivisionId: option?.educationLevelDivisionId,
              }));
              const params = {
                educationLevelDivisionId: option?.educationLevelDivisionId,
                educationLevelId: formData.educationLevelId,
                yearLevelId: formData?.yearLevel?.value,
                classroomId: formData?.classroom?.value,
                assessmentId: option?.value,
              };
              getDataForRecording(params);
            }}
            isDisabled={!formData?.classroom?.value}
            isClearable={false}
            isLoading={assessmentsLoading}
            options={assessments}
            placeholder={"Select Assessments"}
          />
        </form>

        {fetching ? (
          <div className="new-spinner-container">
            <div className="new-spinner">
              <div className="loader">
                <Spinner color="primary" />
              </div>
              <div style={{ fontSize: "16px", marginTop: "15px" }}>
                Please wait !! Processing.....
              </div>
            </div>
          </div>
        ) : subjects?.subjects?.length > 0 ? (
          <>
            <div className="action-btns end">
              <ReportGeneration
                subjects={subjects.subjects}
                studentList={studentList}
                assessmentName={formData?.assessment?.label}
                yearLevelName={formData?.yearLevel?.label}
                classroomName={formData?.classroom?.label}
                studentSubjectAssessments={studentSubjectAssessments}
                scale={subjectAssessmentScale}
              />
            </div>

            <div className="arr-table-container">
              <table aria-label="arr-table" className="arr-table">
                <thead>
                  <tr>
                    <th rowSpan={2}>Name</th>
                    {subjects?.subjects?.map((item) => (
                      <th
                        key={item.id}
                        colSpan={
                          Number(item?.hasTheory) + Number(item?.hasPractical)
                        }
                      >
                        <div
                          className="flex"
                          style={{
                            flexDirection: "column",
                            gap: "12px",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            onClick={() => {
                              setSubjectIdLoader(item?.id);
                              handleSaveAssessmentRecords(
                                "finalize",
                                item?.id,
                                item?.finalized,
                              );
                            }}
                            disabled={
                              loading.finalize || !item?.finalizeClickable
                            }
                            size="sm"
                          >
                            {loading?.finalize &&
                            item?.id === subjectIdLoader ? (
                              <Spinner color="white" size={"sm"} />
                            ) : item?.finalized ? (
                              "Unfinalize"
                            ) : (
                              "Finalize"
                            )}
                          </Button>
                          {item?.name}
                        </div>
                      </th>
                    ))}
                  </tr>
                  <tr>
                    {subjects?.subjects?.map((item) => {
                      const theoryMax =
                        item?.scale?.assessmentScaleMark?.theoryMax ?? null;
                      const practicalMax =
                        item?.scale?.assessmentScaleMark?.practicalMax ?? null;

                      if (item?.hasTheory && item?.hasPractical) {
                        return (
                          <React.Fragment key={item.id}>
                            <th style={{ textAlign: "center" }}>
                              Th{theoryMax != null && `(${theoryMax})`}
                            </th>
                            <th style={{ textAlign: "center" }}>
                              Pr{practicalMax != null && `(${practicalMax})`}
                            </th>
                          </React.Fragment>
                        );
                      }

                      if (item?.hasTheory && !item?.hasPractical) {
                        return (
                          <th style={{ textAlign: "center" }}>
                            Th{theoryMax != null && `(${theoryMax})`}
                          </th>
                        );
                      }

                      if (!item?.hasTheory && item?.hasPractical) {
                        return (
                          <th style={{ textAlign: "center" }}>
                            Pr{practicalMax != null && `(${practicalMax})`}
                          </th>
                        );
                      }
                    })}
                  </tr>
                </thead>
                <tbody>
                  {studentList?.map((student) => (
                    <tr key={student.admissionId}>
                      <td>
                        <div style={{ textAlign: "center" }}>
                          <img
                            style={{ width: "70px", height: "70px" }}
                            src={
                              student?.photo
                                ? URL.imageSelectURL + student?.photo
                                : StudentImg
                            }
                          />
                          <span
                            style={{ width: "100px", margin: "0 auto" }}
                            className="truncate-text"
                          >
                            {student?.fullName}
                          </span>
                        </div>
                      </td>
                      {subjects?.subjects?.map((subject) => {
                        const studentItem = subject?.students?.find(
                          (item) => item.admissionId === student.admissionId,
                        );

                        const assessmentScale =
                          subjectAssessmentScale[subject.id];

                        const isFinalized = subject.finalized;

                        if (!studentItem)
                          return (
                            <React.Fragment
                              key={`${student.admissionId}-${subject.id}`}
                            >
                              {subject?.hasTheory && (
                                <td
                                  style={{ textAlign: "center" }}
                                  key={subject.id}
                                >
                                  N/A
                                </td>
                              )}
                              {subject?.hasPractical && (
                                <td
                                  style={{ textAlign: "center" }}
                                  key={subject.id}
                                >
                                  N/A
                                </td>
                              )}
                            </React.Fragment>
                          );

                        return (
                          <SubjectAssessmentTableRow
                            key={`${student.admissionId}-${subject.id}`}
                            subjectId={subject.id}
                            hasTheory={subject.hasTheory}
                            hasPractical={subject.hasPractical}
                            admissionId={student.admissionId}
                            handleChange={handleChange}
                            scale={assessmentScale}
                            studentSubjectAssessments={
                              studentSubjectAssessments
                            }
                            isFinalized={isFinalized}
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th rowSpan={2}></th>
                    {subjects?.subjects?.map((item) => (
                      <th
                        key={item.id}
                        colSpan={
                          Number(item?.hasTheory) + Number(item?.hasPractical)
                        }
                      >
                        <div
                          className="flex"
                          style={{
                            flexDirection: "column",
                            gap: "12px",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            onClick={() => {
                              setResetModal(true);
                              setResetSubject(item);
                            }}
                            variant="danger"
                            disabled={item?.finalized}
                            size="sm"
                          >
                            Reset
                          </Button>
                        </div>
                      </th>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>

            {permissions?.finalizeReportPermission && (
              <div className="action-btns end">
                <Button
                  onClick={() => handleSaveAssessmentRecords("save")}
                  disabled={loading.insert || areSubjectsFinalized}
                >
                  {loading.insert ? (
                    <Spinner color="white" size={"sm"} />
                  ) : (
                    "Save Assessment Record"
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-data">No Data found</div>
        )}
      </div>

      {/* reset modal */}
      <CustomModal
        isOpen={resetModal}
        toggle={() => setResetModal(false)}
        modalHeader={"Are you sure?"}
        children={
          <div>
            <p>Once reset, you will not be able to recover this record</p>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                gap: "8px",
              }}
            >
              <Button variant="outline" onClick={() => setResetModal(false)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={handleResetSubjectRecord}>
                Reset
              </Button>
            </div>
          </div>
        }
      />
    </>
  );
};

export default SubjectAssessment;
